import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Info } from "lucide-react";

// Styles CSS pour skeleton shimmer
export const shimmerStyle = `
@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
@keyframes rotate {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
.skeleton-shimmer {
  background: linear-gradient(90deg, #2A2A2A 25%, #333 50%, #2A2A2A 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}
.rotate-animation {
  animation: rotate 0.6s ease;
}
`;

// Injecter les styles
if (typeof document !== 'undefined' && !document.getElementById('dashboard-shimmer-styles')) {
  const styleSheet = document.createElement('style');
  styleSheet.id = 'dashboard-shimmer-styles';
  styleSheet.textContent = shimmerStyle;
  document.head.appendChild(styleSheet);
}

// Couleurs du système (theme-aware)
export const COLORS_DARK = {
  positive: "#22C55E", negative: "#EF4444", warning: "#F59E0B", neutral: "#9CA3AF",
  gridLine: "#2A2A2A", axisText: "#6B7280", tooltipBg: "#1A1A1A", tooltipBorder: "#333"
};
export const COLORS_LIGHT = {
  positive: "#16A34A", negative: "#DC2626", warning: "#D97706", neutral: "#6B7280",
  gridLine: "#E5E7EB", axisText: "#9CA3AF", tooltipBg: "#FFFFFF", tooltipBorder: "#E5E7EB"
};

// Textes des tooltips info
export const TOOLTIP_TEXTS = {
  revenue: "Somme de toutes les ventes validées sur la période sélectionnée.",
  sales: "Nombre total de transactions enregistrées à la caisse sur la période.",
  avgBasket: "CA total divisé par le nombre de transactions. Indique la valeur moyenne d'un achat.",
  customers: "Clientes ayant effectué au moins un achat sur la période sélectionnée.",
  chart: "Visualisation jour par jour de votre chiffre d'affaires sur la période choisie.",
  topProducts: "Articles les plus vendus en quantité sur la période. Cliquez pour voir le détail.",
  categories: "Répartition de votre CA par catégorie de produits.",
  alerts: "Détections automatiques basées sur votre stock et vos ventes. Cliquez pour agir.",
  transactions: "Les 5 dernières ventes enregistrées à la caisse.",
  peakActivity: "Tranche horaire avec le plus grand nombre de ventes sur les 30 derniers jours.",
  bestCustomer: "Cliente avec le montant total d'achats le plus élevé sur la période.",
  stockValue: "Quantité en stock × prix de vente pour chaque produit."
};

// Composant Tooltip Info
export function InfoTooltip({ text }) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="relative inline-block ml-2">
      <Info
        className="w-3.5 h-3.5 text-gray-500 cursor-help transition-colors hover:text-gray-400"
        onMouseEnter={() => setVisible(true)}
        onMouseLeave={() => setVisible(false)}
      />
      <AnimatePresence>
        {visible && (
          <motion.div
            initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 5 }}
            transition={{ duration: 0.2 }}
            className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-[#1F2937] text-white text-xs rounded-lg max-w-[220px] whitespace-normal"
            style={{ minWidth: '180px' }}
          >
            {text}
            <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-[#1F2937]" />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Skeleton composant
export function Skeleton({ className = "", width, height }) {
  return (
    <div className={`skeleton-shimmer rounded ${className}`}
      style={{ width: width || '100%', height: height || '20px' }} />
  );
}

// SVG Graphique vide
export function EmptyChartSVG() {
  return (
    <svg width="80" height="80" viewBox="0 0 80 80" fill="none" className="mx-auto mb-3">
      <path d="M10 60 L20 45 L30 55 L40 35 L50 50 L60 30 L70 45" stroke="#333" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <line x1="10" y1="70" x2="70" y2="70" stroke="#333" strokeWidth="2" />
      <line x1="10" y1="70" x2="10" y2="20" stroke="#333" strokeWidth="2" />
    </svg>
  );
}

// SVG Donut vide
export function EmptyDonutSVG() {
  return (
    <svg width="180" height="180" viewBox="0 0 180 180" className="mx-auto">
      <circle cx="90" cy="90" r="65" stroke="#2A2A2A" strokeWidth="35" fill="none" />
      <text x="90" y="85" textAnchor="middle" fill="#6B7280" fontSize="12" fontWeight="500">Pas encore</text>
      <text x="90" y="100" textAnchor="middle" fill="#6B7280" fontSize="12" fontWeight="500">de données</text>
    </svg>
  );
}

// Génération de palette harmonieuse depuis couleur principale
export function generateColorPalette(baseColor, count = 6) {
  const hex = baseColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16) / 255;
  const g = parseInt(hex.substr(2, 2), 16) / 255;
  const b = parseInt(hex.substr(4, 2), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;
  if (max === min) { h = s = 0; }
  else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: h = 0;
    }
  }
  const palette = [];
  for (let i = 0; i < count; i++) {
    const hue = (h + (i * 0.12)) % 1;
    const sat = Math.max(0.3, Math.min(0.9, s + (i % 2 === 0 ? 0.1 : -0.1)));
    const lig = Math.max(0.35, Math.min(0.65, l + (i * 0.05 - 0.1)));
    const c2 = (1 - Math.abs(2 * lig - 1)) * sat;
    const x2 = c2 * (1 - Math.abs((hue * 6) % 2 - 1));
    const m2 = lig - c2 / 2;
    let r1, g1, b1;
    if (hue < 1/6) { r1 = c2; g1 = x2; b1 = 0; }
    else if (hue < 2/6) { r1 = x2; g1 = c2; b1 = 0; }
    else if (hue < 3/6) { r1 = 0; g1 = c2; b1 = x2; }
    else if (hue < 4/6) { r1 = 0; g1 = x2; b1 = c2; }
    else if (hue < 5/6) { r1 = x2; g1 = 0; b1 = c2; }
    else { r1 = c2; g1 = 0; b1 = x2; }
    const toHex = (n) => Math.round((n + m2) * 255).toString(16).padStart(2, '0');
    palette.push(`#${toHex(r1)}${toHex(g1)}${toHex(b1)}`);
  }
  return palette;
}

// Shared reusable modal component
export function DashboardModal({ isOpen, onClose, title, children }) {
  if (!isOpen) return null;
  return (
    <div onClick={onClose} data-testid="dashboard-modal-overlay"
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, backdropFilter: 'blur(4px)' }}>
      <div onClick={e => e.stopPropagation()} data-testid="dashboard-modal-content"
        style={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 16, padding: '28px 24px', maxWidth: 420, width: '90%', position: 'relative', boxShadow: '0 24px 60px rgba(0,0,0,0.4)' }}>
        <button onClick={onClose} data-testid="dashboard-modal-close"
          style={{ position: 'absolute', top: 14, right: 14, background: 'transparent', border: 'none', color: 'hsl(var(--muted-foreground))', fontSize: 18, cursor: 'pointer' }}>
          ✕
        </button>
        <h3 style={{ fontSize: 17, fontWeight: 700, color: 'hsl(var(--foreground))', marginBottom: 12 }}>{title}</h3>
        {children}
      </div>
    </div>
  );
}
